import {
  TextElementMixin,
} from "%/dynamic-component-editor/standard-elements/text-element/text-element-mixin";
import Label from "%/dynamic-component-editor/options/label";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import BasicTextOptions from "%/dynamic-component-editor/bundles/basic-text-options";
import TextOptions from "%/dynamic-component-editor/bundles/text-options";
import SpacingOptions from "%/dynamic-component-editor/bundles/spacing-options";
import LayoutWidthHeightOptions from "%/dynamic-component-editor/bundles/layout-width-height-options";
import BorderOptions from "%/dynamic-component-editor/bundles/border-options";
import BackgroundOptions from "%/dynamic-component-editor/bundles/background-options";
import HrefOption from "%/dynamic-component-editor/options/href-option";
import TypographyBranding from "%/dynamic-component-editor/classes/branding/typography-branding";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import LayoutHeight from "%/dynamic-component-editor/options/layout-height";
import BaseOption from "%/dynamic-component-editor/classes/base-option";
import BackgroundRepeat from "%/dynamic-component-editor/options/background-repeat";
import BackgroundPosition from "%/dynamic-component-editor/options/background-position";
import BackgroundColor from "%/dynamic-component-editor/options/background-color";
import BackgroundSize from "%/dynamic-component-editor/options/background-size";
import BorderRadius from "%/dynamic-component-editor/options/border-radius";
import BorderSides from "%/dynamic-component-editor/options/border-sides";
import BrandedTextStyleOptions from "%/dynamic-component-editor/bundles/branded-text-style-options";
import TextOption from "%/dynamic-component-editor/options/text-option";
import { resolveLinkText } from "%/dynamic-component-editor/helpers/link-text-migration";
import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import LinkType from "%/dynamic-component-editor/options/link-type";
import { resolveContextualPageValue } from "%/dynamic-components/render-engine/options/click-actions/contextual-page-value";
import DynamicComponentError from "%/dynamic-component-editor/errors/dynamic-component-error";
import HrefAction from "%/dynamic-component-editor/options/href-action";
import BackgroundGradient from "%/dynamic-component-editor/options/background-gradient";
import EffectsOptions from "%/dynamic-component-editor/bundles/effects-options";

export const UrlElementMixin = {
  // Hooking UrlElement into the typography branding chain lets the
  // text-branding-style-option (heading / subheading / paragraph / …)
  // resolve through the destination's `BrandedHeadingElement` /
  // `BrandedParagraphElement` / … BrandingElements without morphing
  // the URL element's own type. `TypographyBranding.determineType`
  // reads `text-branding-style-option` off the element's options and
  // returns the matching branded element type, which the chain walk
  // uses for the lookup. Combined with the guard in
  // text-branding-style-option's sideEffect (only morph for actually-
  // morphable types), the URL element keeps its shape and gains the
  // selected typography style.
  brandingType: TypographyBranding,
  mixins: [DynamicElementMixin, TextElementMixin],
  props: {
    href: {
      type: String,
      required: false,
      default: "", // this is for reusability purposes
    },
  },
  data() {
    return {
      HrefOption,
      Label,
      LayoutWidth,
      LayoutHeight,
      BaseOption,
      BackgroundRepeat,
      BackgroundPosition,
      BackgroundColor,
      BackgroundSize,
    };
  },
  optionsOrder: [
    "link-options",
  ],
  optionBundles: [
    new AppearanceOptions({}, {
      "border-radius": false,
    }),
    new BrandedTextStyleOptions({
      "default.text-branding-style-option": "paragraph",
    }),
    BasicTextOptions,
    TextOptions,
    SpacingOptions,
    new LayoutWidthHeightOptions({
      "default.layout-width": "hug",
      "default.layout-height": "hug",
    }, {
      "layout-height": true,
      "max-height": true,
      "min-height": true,
    }),
    BorderOptions,
    new BackgroundOptions({}, {
      "background-image": true,
      "background-gradient": true,
    }),
    EffectsOptions,
  ],
  options: [
    new HrefOption(undefined, true),
    new Label(undefined, true),
    new LinkType(undefined, true),
  ],
  computed: {
    editableText() {
      return false;
    },
    hasLabel() {
      return this.label !== undefined && this.label !== null && this.label !== "";
    },
    label() {
      return "";
    },
    target() {
      return this.getOptionValue(HrefAction)?.openInNewTab ? "_blank" : "_self";
    },
    rel() {
      return "";
    },
    myStyleOverrides() {
      return { ...this.textStyleOverrides, ...this.urlStyleOverrides };
    },
    myBorderRadius() {
      return this.getOptionValue(BorderRadius);
    },
    myBorderSides() {
      return this.getOptionValue(BorderSides);
    },
    urlStyleOverrides() {
      const overrides = {
        backgroundColor: this.getOptionValue(BackgroundColor),
        hoverBackgroundColor: this.getOptionValue(BackgroundColor, BaseOption.$states.HOVER),
      };
      if (this.shouldShowLink) {
        overrides.borderRadius = this.myBorderRadius;
        overrides.backgroundGradient = this.getOptionValue(BackgroundGradient);
        overrides.backgroundGradientHover = this.getOptionValue(BackgroundGradient, BaseOption.$states.HOVER);
      }
      return overrides;
    },
    computedHref() {
      const linkValues = this.getOptionValue(HrefAction);
      if (!linkValues) {
        return "";
      }
      const urlType = linkValues.linkType;
      let urlValue = linkValues.value;

      if (typeof urlValue === "string" && urlValue.startsWith("http")) {
        return urlValue;
      } else if (typeof urlValue === "object") {
        urlValue = urlValue.value;
      }

      switch (urlType) {
        case LinkType.LINK_TYPE_HYPERLINK:
          return urlValue;
        case LinkType.LINK_TYPE_PAGE:
          urlValue = resolveContextualPageValue(urlValue);
          if (urlValue === null || urlValue === undefined) {
            return;
          }
          if (urlValue.startsWith("/")) {
            urlValue = urlValue.substring(1);
          }
          return `${this.links.web}/${urlValue}`;
        case LinkType.LINK_TYPE_STORY:
          return `${this.links.web}/story/${urlValue}`;
        case LinkType.LINK_TYPE_HASHTAG:
          return `${this.links.web}/stories/${urlValue}`;
        default:
          throw new DynamicComponentError("Url type not found", {
            urlType,
            urlValue,
          });
      }
    },
    shouldShowLink() {
      return true;
    },
    changeListMessage() {
      let message = `Element label value updated for ${this.dynamicComponentName}`;
      if (this.isPage) {
        message = `Element label value updated for the ${this.dynamicComponentName}`;
      }
      return message;
    },
    textValueOptionClass() {
      return TextOption;
    },
    textValueKey() {
      return undefined;
    },
    // Links authored before the text moved still hold it on
    // `href-action.label`; without this they render blank in the editor.
    textOptionValue() {
      return resolveLinkText(this.getOptionValue(TextOption), this.getOptionValue(HrefAction));
    },
  },
};
